/**
 * Events Manager Pro's own attendee-forms.js trims excess attendee field groups off a
 * static NodeList snapshot when the "Spaces" select is decreased, so it always removes
 * the same node repeatedly instead of the right number of nodes — any decrease of more
 * than 1 space leaves stale .em-ticket-booking groups behind. We can't edit the pro
 * add-on directly (a plugin update would wipe the change), so this runs as a second,
 * idempotent pass after the plugin's own change handler and trims whatever is left.
 */
export function initEventsManagerTicketSpacesSync() {
  document.addEventListener('change', function (e) {
    if (!e.target.matches('.em-ticket-select')) return;

    const spaces = parseInt(e.target.value, 10) || 0;
    const ticketId = e.target.dataset.ticketId;
    const form = e.target.closest('form.em-booking-form');
    if (!form || !ticketId) return;

    const container = form.querySelector('.em-ticket-bookings-' + ticketId);
    if (!container) return;

    let bookings = container.querySelectorAll('.em-ticket-booking:not(.em-ticket-booking-template)');
    while (bookings.length > spaces) {
      bookings[bookings.length - 1].remove();
      bookings = container.querySelectorAll('.em-ticket-booking:not(.em-ticket-booking-template)');
    }

    container.querySelectorAll('.em-ticket-booking:not(.em-ticket-booking-template)').forEach((el, i) => {
      el.querySelectorAll('[data-placeholder]').forEach((ph) => {
        ph.innerHTML = ph.getAttribute('data-placeholder').replace(/%d/g, i + 1);
      });
    });
  });
}

/**
 * Defer Stripe Elements (PaymentIntent creation) until the user explicitly
 * chooses a payment gateway.
 *
 * EMP auto-inits the selected gateway when a paid booking intent appears:
 * - Multi-gateway <select> defaults to Credit Card
 * - Single-gateway (guests: invoice hidden) uses a hidden input that counts as selected
 *
 * Theme forces radio selectors where EMP renders multiple gateways. For the
 * single-gateway case we promote the hidden Stripe input to an unchecked radio
 * so Elements only mounts after an explicit choice. We also block programmatic
 * `em_gateway_selected_stripe_*` events until a radio is actually checked.
 */
export function initEventsManagerDeferredGatewayInit() {
  const gatewayRadioSelector = 'input[type="radio"].em-payment-gateway-option';
  const stripeSelectedEvents = [
    'em_gateway_selected_stripe_elements',
    'em_gateway_selected_stripe_checkout',
  ];

  function gatewayLabel(value) {
    if (value === 'stripe_elements' || value === 'stripe_checkout') {
      return 'Pay with Credit Card';
    }
    if (value === 'offline') {
      return 'Pay with Invoice';
    }
    return 'Pay with ' + value;
  }

  /**
   * Guests only see Stripe (invoice is filtered out). EMP then outputs a hidden
   * gateway input which is treated as already selected and creates a PaymentIntent
   * immediately. Promote it to an unchecked radio so choice is explicit.
   */
  function promoteHiddenGatewayToRadio(gateways) {
    const hidden = gateways.querySelector('input[type="hidden"].em-payment-gateway-option');
    if (!hidden || gateways.querySelector(gatewayRadioSelector)) {
      return;
    }

    let selector = gateways.querySelector('.em-payment-gateway-selector');
    if (!selector) {
      selector = document.createElement('p');
      selector.className = 'em-payment-gateway-selector';
      const label = document.createElement('label');
      label.textContent = 'Pay With';
      selector.appendChild(label);
      gateways.insertBefore(selector, gateways.firstChild);
    }

    let fieldset = selector.querySelector('fieldset.em-payment-gateway-options');
    if (!fieldset) {
      fieldset = document.createElement('fieldset');
      fieldset.className = 'em-payment-gateway-options';
      selector.appendChild(fieldset);
    }

    const wrap = document.createElement('div');
    wrap.className = 'em-payment-gateway-option-radio';

    hidden.type = 'radio';
    hidden.checked = false;
    hidden.classList.add('em-payment-gateway');
    hidden.removeAttribute('data-initialized');

    const label = document.createElement('label');
    label.textContent = gatewayLabel(hidden.value);

    wrap.appendChild(hidden);
    wrap.appendChild(label);
    fieldset.appendChild(wrap);
  }

  function associateRadioLabels(gateways) {
    gateways.querySelectorAll('.em-payment-gateway-option-radio').forEach((wrap, i) => {
      const radio = wrap.querySelector(gatewayRadioSelector);
      if (!radio) return;

      if (!radio.id) {
        radio.id = 'em-gateway-radio-' + (radio.value || i) + '-' + i;
      }

      const label = wrap.querySelector('label');
      if (label && !label.getAttribute('for')) {
        label.setAttribute('for', radio.id);
      }

      wrap.classList.add('em-payment-gateway-option-radio--' + (radio.value || 'unknown'));
    });
  }

  function prepareGatewayUi(bookingForm, { resetSelection = false } = {}) {
    bookingForm.querySelectorAll('.em-payment-gateways').forEach((gateways) => {
      promoteHiddenGatewayToRadio(gateways);
      associateRadioLabels(gateways);

      if (resetSelection) {
        gateways.querySelectorAll(gatewayRadioSelector).forEach((radio) => {
          radio.checked = false;
          radio.removeAttribute('data-initialized');
        });
      }

      const checked = gateways.querySelector(gatewayRadioSelector + ':checked');
      if (!checked) {
        gateways.querySelectorAll('.em-payment-gateway-form').forEach((panel) => {
          panel.classList.add('hidden');
        });
      }
    });
  }

  function hasPaidIntent(bookingForm) {
    const intent = bookingForm.querySelector('input.em-booking-intent');
    if (!intent) return false;
    return parseFloat(intent.getAttribute('data-amount') || '0') > 0;
  }

  function hasGatewayChoice(bookingForm) {
    return !!bookingForm.querySelector(gatewayRadioSelector + ':checked');
  }

  function showGatewayRequiredError(bookingForm) {
    const message = 'Please choose a payment method.';
    if (typeof window.em_booking_form_add_error === 'function') {
      window.em_booking_form_add_error(bookingForm, message);
      return;
    }

    const parent = bookingForm.parentElement;
    if (!parent) return;
    parent.querySelectorAll('.em-booking-message').forEach((el) => el.remove());
    const div = document.createElement('div');
    div.className = 'em-booking-message em-booking-message-error';
    div.setAttribute('role', 'alert');
    div.textContent = message;
    parent.prepend(div);
  }

  function bindStripeInitGuard(bookingForm) {
    if (bookingForm.dataset.ebowStripeInitGuard === '1') {
      return;
    }
    bookingForm.dataset.ebowStripeInitGuard = '1';

    stripeSelectedEvents.forEach((eventName) => {
      bookingForm.addEventListener(
        eventName,
        function (e) {
          const checked = bookingForm.querySelector(gatewayRadioSelector + ':checked');
          const expected =
            eventName === 'em_gateway_selected_stripe_checkout' ? 'stripe_checkout' : 'stripe_elements';
          if (!checked || checked.value !== expected) {
            e.stopImmediatePropagation();
          }
        },
        true
      );
    });
  }

  document.addEventListener('em_booking_form_init', function (e) {
    const bookingForm = e.target;
    if (!(bookingForm instanceof HTMLElement) || !bookingForm.matches('form.em-booking-form')) {
      return;
    }

    prepareGatewayUi(bookingForm, { resetSelection: true });
    bindStripeInitGuard(bookingForm);

    if (bookingForm.dataset.ebowGatewaySubmitGuard === '1') {
      return;
    }
    bookingForm.dataset.ebowGatewaySubmitGuard = '1';

    bookingForm.addEventListener(
      'submit',
      function (submitEvent) {
        if (!hasPaidIntent(bookingForm)) return;
        if (hasGatewayChoice(bookingForm)) return;

        submitEvent.preventDefault();
        submitEvent.stopPropagation();
        submitEvent.stopImmediatePropagation();
        showGatewayRequiredError(bookingForm);

        const gateways = bookingForm.querySelector('.em-payment-gateways');
        if (gateways) {
          gateways.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },
      true
    );
  });

  // EMP reveals .em-payment-gateways on paid intent updates and may leave the
  // first gateway panel visible from PHP even when no radio is checked. Also
  // re-promote a hidden gateway if the payment section was injected later.
  // Do not clear an already-chosen radio (e.g. ticket quantity change).
  document.addEventListener('em_booking_intent_updated', function (e) {
    const bookingForm = e.detail && e.detail.form ? e.detail.form : e.target;
    if (!(bookingForm instanceof HTMLElement)) return;

    prepareGatewayUi(bookingForm, { resetSelection: false });
    bindStripeInitGuard(bookingForm);
  });
}
